import { useState, type FormEvent } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { couponsApi } from '../api/coupons';
import { toErrorMessage } from '../api/client';
import { ActiveBadge, Card, PageHead } from '../components/ui';
import { Empty, ErrorState, Loading } from '../components/DataStates';
import { Modal } from '../components/Modal';
import type {
  Coupon,
  CouponCreateRequest,
  CouponDiscountType,
} from '../types';

function formatValue(type: CouponDiscountType, value: number): string {
  return type === 'PERCENT' ? `${value}%` : `₹${value.toFixed(2)}`;
}

function formatWindow(from: string | null, until: string | null): string {
  const fmt = (s: string) => new Date(s).toLocaleString();
  if (!from && !until) return 'Always';
  if (from && until) return `${fmt(from)} → ${fmt(until)}`;
  if (from) return `From ${fmt(from)}`;
  return `Until ${fmt(until!)}`;
}

export function CouponsPage() {
  const qc = useQueryClient();
  const [creating, setCreating] = useState(false);
  const [editing, setEditing] = useState<Coupon | null>(null);

  const q = useQuery({ queryKey: ['coupons'], queryFn: () => couponsApi.list() });

  const invalidate = () => qc.invalidateQueries({ queryKey: ['coupons'] });

  const deactivate = useMutation({
    mutationFn: (id: number) => couponsApi.deactivate(id),
    onSuccess: invalidate,
  });

  return (
    <>
      <PageHead
        title="Coupons"
        subtitle="Discount codes cashiers can apply on the cart. A valid coupon overrides the cashier discount limit."
        actions={
          <button className="btn btn-primary" onClick={() => setCreating(true)}>
            + New Coupon
          </button>
        }
      />

      <Card>
        {q.isLoading ? (
          <Loading />
        ) : q.isError ? (
          <ErrorState message={toErrorMessage(q.error)} onRetry={q.refetch} />
        ) : !q.data || q.data.length === 0 ? (
          <Empty icon="🏷️" title="No coupons yet" />
        ) : (
          <div className="table-wrap">
            <table className="data">
              <thead>
                <tr>
                  <th>Code</th>
                  <th>Discount</th>
                  <th>Valid</th>
                  <th>Status</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {q.data.map((c) => (
                  <tr key={c.id}>
                    <td>
                      <strong className="mono">{c.code}</strong>
                    </td>
                    <td>{formatValue(c.discountType, c.discountValue)}</td>
                    <td className="muted">
                      {formatWindow(c.validFrom, c.validUntil)}
                    </td>
                    <td>
                      <ActiveBadge active={c.active} />
                    </td>
                    <td className="text-right">
                      <button
                        className="btn btn-secondary btn-sm"
                        onClick={() => setEditing(c)}
                      >
                        Edit
                      </button>{' '}
                      {c.active && (
                        <button
                          className="btn btn-danger btn-sm"
                          onClick={() => {
                            if (window.confirm(`Deactivate coupon “${c.code}”?`))
                              deactivate.mutate(c.id);
                          }}
                        >
                          Deactivate
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>

      {creating && (
        <CouponFormModal
          title="New Coupon"
          onClose={() => setCreating(false)}
          onSaved={() => {
            setCreating(false);
            invalidate();
          }}
        />
      )}

      {editing && (
        <CouponFormModal
          title={`Edit ${editing.code}`}
          existing={editing}
          onClose={() => setEditing(null)}
          onSaved={() => {
            setEditing(null);
            invalidate();
          }}
        />
      )}
    </>
  );
}

// datetime-local <-> ISO helpers. The backend stores naive-IST; the input value
// "YYYY-MM-DDTHH:MM" is sent as-is (treated as IST) and displayed by slicing the
// ISO string's local part.
function toInput(iso: string | null | undefined): string {
  return iso ? iso.slice(0, 16) : '';
}

function CouponFormModal({
  title,
  existing,
  onClose,
  onSaved,
}: {
  title: string;
  existing?: Coupon;
  onClose: () => void;
  onSaved: () => void;
}) {
  const [code, setCode] = useState(existing?.code ?? '');
  const [discountType, setDiscountType] = useState<CouponDiscountType>(
    existing?.discountType ?? 'PERCENT',
  );
  const [discountValue, setDiscountValue] = useState<string>(
    existing ? String(existing.discountValue) : '',
  );
  const [validFrom, setValidFrom] = useState(toInput(existing?.validFrom));
  const [validUntil, setValidUntil] = useState(toInput(existing?.validUntil));
  const [active, setActive] = useState(existing?.active ?? true);
  const [error, setError] = useState<string | null>(null);

  const mutation = useMutation({
    mutationFn: () => {
      const value = Number(discountValue);
      if (existing) {
        return couponsApi.update(existing.id, {
          discountType,
          discountValue: value,
          validFrom: validFrom || null,
          validUntil: validUntil || null,
          active,
        });
      }
      const body: CouponCreateRequest = {
        code: code.trim().toUpperCase(),
        discountType,
        discountValue: value,
        validFrom: validFrom || null,
        validUntil: validUntil || null,
        active,
      };
      return couponsApi.create(body);
    },
    onSuccess: onSaved,
    onError: (err) => setError(toErrorMessage(err)),
  });

  const valueNum = Number(discountValue);
  const valid =
    (existing || code.trim().length >= 2) &&
    discountValue !== '' &&
    valueNum > 0 &&
    !(discountType === 'PERCENT' && valueNum > 100);

  function onSubmit(e: FormEvent) {
    e.preventDefault();
    setError(null);
    mutation.mutate();
  }

  return (
    <Modal
      open
      title={title}
      onClose={onClose}
      footer={
        <>
          <button className="btn btn-ghost" onClick={onClose}>
            Cancel
          </button>
          <button
            className="btn btn-primary"
            onClick={onSubmit}
            disabled={mutation.isPending || !valid}
          >
            {mutation.isPending ? 'Saving…' : 'Save'}
          </button>
        </>
      }
    >
      <form onSubmit={onSubmit}>
        {error && <div className="form-error">{error}</div>}
        {!existing && (
          <div className="field">
            <label>Code *</label>
            <input
              value={code}
              onChange={(e) => setCode(e.target.value.toUpperCase())}
              placeholder="e.g. SAVE10"
              autoFocus
              required
            />
          </div>
        )}
        <div className="field">
          <label>Discount type *</label>
          <select
            value={discountType}
            onChange={(e) =>
              setDiscountType(e.target.value as CouponDiscountType)
            }
          >
            <option value="PERCENT">Percentage (%)</option>
            <option value="FIXED">Fixed amount (₹)</option>
          </select>
        </div>
        <div className="field">
          <label>{discountType === 'PERCENT' ? 'Percent off *' : 'Amount off (₹) *'}</label>
          <input
            type="number"
            step="0.01"
            min="0"
            value={discountValue}
            onChange={(e) => setDiscountValue(e.target.value)}
            placeholder={discountType === 'PERCENT' ? '0 - 100' : 'Amount'}
            required
          />
        </div>
        <div className="field">
          <label>Valid from (optional)</label>
          <input
            type="datetime-local"
            value={validFrom}
            onChange={(e) => setValidFrom(e.target.value)}
          />
        </div>
        <div className="field">
          <label>Valid until (optional)</label>
          <input
            type="datetime-local"
            value={validUntil}
            onChange={(e) => setValidUntil(e.target.value)}
          />
        </div>
        <div className="checkbox-row">
          <input
            id="couponActive"
            type="checkbox"
            checked={active}
            onChange={(e) => setActive(e.target.checked)}
          />
          <label htmlFor="couponActive">Active</label>
        </div>
      </form>
    </Modal>
  );
}
